import type { JSX } from 'react';
import { useState } from 'react';
import { App, Input, Modal, Popconfirm, Select, Table } from 'antd';
import type { TableColumnsType } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { Button, Panel, Tag, plural } from './ds';
import { analyticsApi } from '../lib/api/admin';
import { editorApi, wikiAdminApi, type VerifyQueueRow } from '../lib/wikiApi';
import { buildOwnerOptions, parseOwnerValue } from '../lib/article-owner';
import '../pages/analytics/analytics.css';

type QueueKey = 'overdue' | 'expiring' | 'noOwner' | 'fresh' | 'stale';

const TITLES: Record<QueueKey, [string, string]> = {
  overdue: ['Просрочена проверка', 'владелец не подтвердил актуальность в срок'],
  expiring: ['Истекает за 14 дней', 'напомним владельцу заранее — чем короче интервал, тем ближе к сроку'],
  noOwner: ['Без владельца', 'некому подтверждать — назначьте человека или отдел'],
  fresh: ['Актуальны', 'последние подтверждённые'],
  stale: ['Кандидаты в архив', '180 дней без чтений и просрочка'],
};

/**
 * «Актуальность базы» (фаза 3.4, Guru-style verify; вид HUB 2.0): карточки
 * очередей — просрочено / истекает / без владельца / актуальны / кандидаты в
 * архив, — таблица выбранной очереди с действиями по строке («Актуально»,
 * «Назначить/Сменить», «В архив») и пачкой, «Напомнить владельцам» (то же,
 * что ежедневный крон: за 7 дней до срока и не чаще раза в 7 дней по статье,
 * для коротких интервалов — чаще). Владелец — человек или отдел УК; в «Без
 * владельца» попадает и отдел, в котором никого нет. Используется на
 * /admin/freshness, во вкладке аналитики и в администрировании.
 */
export function VerifyQueueTab(): JSX.Element {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const queueQ = useQuery({ queryKey: ['wiki-admin', 'verify-queue'], queryFn: () => analyticsApi.verifyQueue() });
  const staleQ = useQuery({ queryKey: ['wiki-admin', 'stale'], queryFn: () => wikiAdminApi.staleArticles() });
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['wiki-admin', 'verify-queue'] });
    void qc.invalidateQueries({ queryKey: ['wiki-admin', 'stale'] });
    void qc.invalidateQueries({ queryKey: ['wiki', 'admin', 'verify-counts'] });
  };

  const [key, setKey] = useState<QueueKey>('overdue');
  const [selected, setSelected] = useState<string[]>([]);
  const [ownerFor, setOwnerFor] = useState<string[] | null>(null);
  // Выбранный владелец: id человека или `dept:<код>` отдела (см. lib/article-owner).
  const [ownerValue, setOwnerValue] = useState<string | null>(null);
  const [ownerSearch, setOwnerSearch] = useState('');
  const usersQ = useQuery({
    queryKey: ['wiki-editor', 'users', ownerSearch],
    queryFn: () => editorApi.searchUsers(ownerSearch),
    enabled: ownerFor != null,
  });
  const deptsQ = useQuery({
    queryKey: ['wiki', 'editor-departments'],
    queryFn: () => editorApi.ownerDepartments(),
    enabled: ownerFor != null,
    staleTime: 5 * 60_000,
  });
  const [archiveFor, setArchiveFor] = useState<string[] | null>(null);
  const [replaceSlug, setReplaceSlug] = useState('');

  const done = (text: string): void => {
    void message.success(text);
    setSelected([]);
    invalidate();
  };
  const ownerMut = useMutation({
    mutationFn: (ids: string[]) => {
      const choice = parseOwnerValue(ownerValue);
      if (choice.kind === 'none') throw new Error('Выберите владельца');
      return wikiAdminApi.assignOwner(ids, choice.kind === 'dept' ? { ownerDeptCode: choice.code } : { ownerId: choice.id });
    },
    onSuccess: (r) => {
      setOwnerFor(null);
      setOwnerValue(null);
      done(`Владелец назначен: ${r.updated}${r.failed.length ? `, ошибок ${r.failed.length}` : ''}`);
    },
    onError: (e: Error) => void message.error(e.message),
  });
  const verifyMut = useMutation({
    mutationFn: (ids: string[]) => wikiAdminApi.verifyBulk(ids),
    onSuccess: (r) => done(`Подтверждено актуальными: ${r.updated}${r.failed.length ? `, ошибок ${r.failed.length}` : ''}`),
    onError: (e: Error) => void message.error(e.message),
  });
  const archiveMut = useMutation({
    mutationFn: (ids: string[]) => wikiAdminApi.archiveBulk(ids, replaceSlug.trim() || null),
    onSuccess: (r) => {
      setArchiveFor(null);
      setReplaceSlug('');
      done(`В архив: ${r.archived}${r.failed.length ? `, ошибок ${r.failed.length}` : ''}`);
      void qc.invalidateQueries({ queryKey: ['wiki'] });
    },
    onError: (e: Error) => void message.error(e.message),
  });
  const remindMut = useMutation({
    mutationFn: () => analyticsApi.verifyRemind(),
    onSuccess: (r) =>
      void message.success(
        r.reminded ? `Напоминания отправлены: ${r.reminded} ${plural(r.reminded, 'статья', 'статьи', 'статей')}` : 'Напоминать пока некому: всем владельцам уже напоминали недавно',
      ),
    onError: (e: Error) => void message.error(e.message),
  });

  const data = queueQ.data;
  const counts = data?.counts;
  const lists: Record<QueueKey, VerifyQueueRow[]> = {
    overdue: data?.overdue ?? [],
    expiring: data?.expiring ?? [],
    noOwner: data?.noOwner ?? [],
    fresh: data?.fresh ?? [],
    stale: staleQ.data ?? [],
  };
  const values: Record<QueueKey, number | undefined> = {
    overdue: counts?.overdue,
    expiring: counts?.expiring,
    noOwner: counts?.noOwner,
    fresh: counts?.fresh,
    stale: staleQ.data?.length,
  };
  const colors: Record<QueueKey, string> = {
    overdue: 'var(--fk-error)',
    expiring: 'var(--fk-warning-text)',
    noOwner: 'var(--fk-chart-5)',
    fresh: 'var(--fk-success-text)',
    stale: 'var(--fk-text-2)',
  };
  const rows = lists[key];

  const columns: TableColumnsType<VerifyQueueRow> = [
    {
      title: 'Статья',
      dataIndex: 'title',
      render: (v: string, r) => (
        <>
          <Link to={`/article/${r.slug}`} style={{ fontWeight: 600 }}>{v}</Link>
          <div className="fk-adm-note">{[r.type, ...r.blocks].join(' · ')}</div>
        </>
      ),
    },
    {
      title: 'Владелец',
      dataIndex: 'ownerName',
      width: 180,
      // «Отдел: <название>» приходит с бэкенда; у отдела без людей там же пояснение в скобках.
      render: (v: string | null, r) =>
        v ? <span style={r.hasOwner === false ? { color: 'var(--fk-error)' } : undefined}>{v}</span> : <span style={{ color: 'var(--fk-error)' }}>Не назначен</span>,
    },
    {
      title: 'Срок',
      dataIndex: 'dueAt',
      width: 170,
      render: (v: string | null, r) =>
        v && r.daysLeft != null ? (
          <span style={{ color: r.daysLeft < 0 ? 'var(--fk-error)' : r.daysLeft <= 7 ? 'var(--fk-warning-text)' : 'var(--fk-text-2)' }}>
            {r.daysLeft < 0 ? `просрочено ${-r.daysLeft} дн.` : `через ${r.daysLeft} дн.`}
            <div className="fk-adm-note">{new Date(v).toLocaleDateString('ru-RU')}</div>
          </span>
        ) : (
          <Tag>не задан</Tag>
        ),
    },
    { title: 'Просмотры', dataIndex: 'viewCount', width: 100, align: 'right', render: (v: number) => v.toLocaleString('ru-RU') },
    {
      title: '',
      key: 'act',
      width: 300,
      render: (_: unknown, r) => (
        <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
          {key !== 'fresh' && (
            <Button size="sm" variant="ghost" loading={verifyMut.isPending && verifyMut.variables?.[0] === r.id} onClick={() => verifyMut.mutate([r.id])}>
              Актуально
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setOwnerFor([r.id])}>
            {r.ownerId || r.ownerDeptCode ? 'Сменить' : 'Назначить'}
          </Button>
          <Button size="sm" variant="text" onClick={() => setArchiveFor([r.id])}>
            В архив
          </Button>
        </span>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="fk-adm-grid4">
        {(Object.keys(TITLES) as QueueKey[]).map((k) => (
          <button
            key={k}
            type="button"
            className="fk-adm-pick"
            aria-pressed={key === k}
            onClick={() => {
              setKey(k);
              setSelected([]);
            }}
          >
            <span className="fk-adm-pick-label">{TITLES[k][0]}</span>
            <span className="fk-adm-pick-value" style={{ color: colors[k] }}>{values[k] ?? '…'}</span>
            <span className="fk-adm-pick-hint">
              {k === 'fresh' && counts?.total != null ? `из ${counts.total.toLocaleString('ru-RU')} статей` : TITLES[k][1]}
            </span>
          </button>
        ))}
      </div>

      <Panel
        title={TITLES[key][0]}
        sub={
          key === 'fresh'
            ? 'показаны последние подтверждённые'
            : 'действия применяются сразу; срок = последнее подтверждение (или публикация) + «раз в N дней» из свойств статьи'
        }
        pad={0}
        right={
          <span style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
            {selected.length > 0 && (
              <>
                <span className="fk-adm-note" style={{ alignSelf: 'center' }}>Выбрано: {selected.length}</span>
                {key !== 'fresh' && (
                  <Popconfirm title={`Подтвердить актуальность ${selected.length} статей?`} onConfirm={() => verifyMut.mutate(selected)} okText="Да" cancelText="Нет">
                    <Button size="sm" variant="soft" loading={verifyMut.isPending}>Актуально</Button>
                  </Popconfirm>
                )}
                <Button size="sm" variant="ghost" onClick={() => setOwnerFor(selected)}>Назначить владельца</Button>
                <Button size="sm" variant="danger" onClick={() => setArchiveFor(selected)}>В архив</Button>
              </>
            )}
            <Button size="sm" variant="ghost" icon={<BellOutlined />} loading={remindMut.isPending} onClick={() => remindMut.mutate()}>
              Напомнить владельцам
            </Button>
          </span>
        }
      >
        <div className="fk-ds-table" style={{ border: 'none', boxShadow: 'none', borderRadius: 0 }}>
          <Table<VerifyQueueRow>
            rowKey="id"
            size="middle"
            loading={key === 'stale' ? staleQ.isLoading : queueQ.isLoading}
            dataSource={rows}
            columns={columns}
            rowSelection={{ selectedRowKeys: selected, onChange: (keys) => setSelected(keys as string[]) }}
            pagination={{ defaultPageSize: 20, showSizeChanger: true }}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: key === 'fresh' ? 'Пока никто не подтверждал' : 'Очередь пуста — всё актуально' }}
          />
        </div>
      </Panel>

      <Modal
        title="Назначить владельца"
        open={ownerFor != null}
        onCancel={() => {
          setOwnerFor(null);
          setOwnerValue(null);
        }}
        onOk={() => ownerFor && ownerValue && ownerMut.mutate(ownerFor)}
        okButtonProps={{ disabled: !ownerValue }}
        confirmLoading={ownerMut.isPending}
        okText="Назначить"
        cancelText="Отмена"
        destroyOnHidden
      >
        <p className="fk-adm-note" style={{ fontSize: 13 }}>
          Статей: {ownerFor?.length ?? 0}. Владелец — человек или отдел: он отвечает за актуальность и получает напоминания
          (у отдела — руководитель и участники).
        </p>
        <Select
          showSearch
          style={{ width: '100%' }}
          placeholder="Выберите отдел или начните вводить фамилию"
          filterOption={false}
          onSearch={setOwnerSearch}
          loading={usersQ.isLoading || deptsQ.isLoading}
          value={ownerValue ?? undefined}
          onChange={(v) => setOwnerValue((v as string | undefined) ?? null)}
          options={buildOwnerOptions({
            depts: deptsQ.data ?? [],
            users: usersQ.data ?? [],
            search: ownerSearch,
            current: { ownerId: null, ownerDept: null, name: null },
          })}
          optionLabelProp="short"
        />
      </Modal>

      <Modal
        title="Отправить в архив"
        open={archiveFor != null}
        onCancel={() => setArchiveFor(null)}
        onOk={() => archiveFor && archiveMut.mutate(archiveFor)}
        confirmLoading={archiveMut.isPending}
        okText="В архив"
        okButtonProps={{ danger: true }}
        cancelText="Отмена"
        destroyOnHidden
      >
        <p className="fk-adm-note" style={{ fontSize: 13 }}>
          Статей: {archiveFor?.length ?? 0}. Архивная статья по прямой ссылке откроется с баннером «в архиве»; если указать slug замены —
          со ссылкой на актуальную статью. Восстановить можно из «Архива».
        </p>
        <Input value={replaceSlug} onChange={(e) => setReplaceSlug(e.target.value)} placeholder="Slug статьи-замены (необязательно)" />
      </Modal>
    </div>
  );
}
